import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Download, RefreshCw, WifiOff } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPublicVersion,
  nativeSessionSafetyUnavailable,
  subscribeNativeSessionSafety,
} from "./api";
import {
  initializeNativeRuntime,
  isNativeRuntime,
  listenForNativeNetwork,
  nativeAppInfo,
  clearNativeClientUpdatePolicy,
  type NativeClientUpdateState,
  nativeClientUpdateState,
  nativeNetworkStatus,
  setNativeClientUpdatePolicy,
  subscribeNativeClientUpdateState,
} from "./nativeRuntime";

type NativeGatewayStatus = "pending" | "error" | "success";
type NativeRuntimeStatus = "pending" | "error" | "success";
type NativeNetworkHandle = { remove(): Promise<void> };

export type NativeRuntimeSnapshot =
  | { status: "success"; connected: boolean | null; appVersion: string | null }
  | { status: "error"; connected: null; appVersion: null };

export interface NativeRuntimeNoticeInput {
  native: boolean;
  connected: boolean | null;
  runtimeStatus: NativeRuntimeStatus;
  gatewayStatus: NativeGatewayStatus;
  installedVersion: string | null;
  serviceVersion: string | null;
  minimumSupportedVersion?: string | null;
  updateState?: NativeClientUpdateState;
  sessionSafetyUnavailable?: boolean;
}

export interface NativeRuntimeNoticeModel {
  kind:
    | "DEVICE_OFFLINE"
    | "RUNTIME_CHECK_FAILED"
    | "GATEWAY_UNREACHABLE"
    | "VERSION_CHECK_PENDING"
    | "SESSION_SAFETY_UNAVAILABLE"
    | "UPDATE_AVAILABLE"
    | "UPDATE_REQUIRED"
    | "SERVER_UPDATE_REQUIRED";
  title: string;
  description: string;
  canRetry: boolean;
}

export async function readNativeRuntimeSnapshot(
  readNetwork: typeof nativeNetworkStatus = nativeNetworkStatus,
  readAppInfo: typeof nativeAppInfo = nativeAppInfo,
  initializeRuntime: typeof initializeNativeRuntime = initializeNativeRuntime,
): Promise<NativeRuntimeSnapshot> {
  try {
    if (!(await initializeRuntime())) {
      return { status: "error", connected: null, appVersion: null };
    }
    const [network, info] = await Promise.all([readNetwork(), readAppInfo()]);
    return {
      status: "success",
      connected: network?.connected ?? null,
      appVersion: info?.version ?? null,
    };
  } catch {
    return { status: "error", connected: null, appVersion: null };
  }
}

export function nativeRuntimeStatusAfterCheck(
  snapshot: NativeRuntimeSnapshot,
  listenerAvailable: boolean,
): NativeRuntimeStatus {
  return snapshot.status === "error" || !listenerAvailable ? "error" : "success";
}

export function nativeRuntimeNoticeModel(
  input: NativeRuntimeNoticeInput,
): NativeRuntimeNoticeModel | null {
  if (!input.native) return null;
  if (input.sessionSafetyUnavailable) {
    return {
      kind: "SESSION_SAFETY_UNAVAILABLE",
      title: "手机安全会话状态无法确认",
      description:
        "系统已停止新的收款、退款、库存和台单操作。请完全关闭并重新打开 App；不要重复提交刚才的业务。",
      canRetry: false,
    };
  }
  if (input.connected === false) {
    return {
      kind: "DEVICE_OFFLINE",
      title: "手机网络已断开",
      description:
        "当前无法连接门店服务，不能提交新操作。已打开内容只供查看；恢复网络后系统会自动重新检查。",
      canRetry: false,
    };
  }
  if (input.runtimeStatus === "error") {
    return {
      kind: "RUNTIME_CHECK_FAILED",
      title: "App 运行环境检查失败",
      description:
        "系统暂时无法确认手机网络或 App 状态。不要重复提交收款、退款或库存操作；请点击重新检查。",
      canRetry: true,
    };
  }
  if (input.gatewayStatus === "error") {
    return {
      kind: "GATEWAY_UNREACHABLE",
      title: "暂时无法连接门店系统",
      description:
        input.connected === true
          ? "手机已有网络，但门店服务没有响应。不要重复提交收款、退款或库存操作；请重新检查连接状态。"
          : "当前无法确认手机网络，门店服务也没有响应。不要重复提交收款、退款或库存操作；请重新检查连接状态。",
      canRetry: true,
    };
  }
  if (input.updateState === "POLICY_PENDING") {
    return {
      kind: "VERSION_CHECK_PENDING",
      title: "正在核对 App 版本",
      description: "核对完成前暂不提交新的经营操作；查看记录、登录和安全退出仍可使用。",
      canRetry: false,
    };
  }
  if (input.installedVersion && input.serviceVersion) {
    const updateState =
      input.updateState ??
      nativeClientUpdateState(input.installedVersion, {
        latestVersion: input.serviceVersion,
        minimumSupportedVersion: input.minimumSupportedVersion ?? "0.0.0",
      });
    if (updateState === "UPDATE_REQUIRED") {
      return {
        kind: "UPDATE_REQUIRED",
        title: "当前 App 必须升级后才能继续营业",
        description: `当前 App v${input.installedVersion}，门店最低支持 v${input.minimumSupportedVersion ?? input.serviceVersion}。为避免重复收款、错账或库存异常，已停止提交新的经营操作；仍可查看记录并安全退出账号。`,
        canRetry: true,
      };
    }
    if (updateState === "SERVER_UPDATE_REQUIRED") {
      return {
        kind: "SERVER_UPDATE_REQUIRED",
        title: "门店服务需要先完成升级核对",
        description: `当前 App v${input.installedVersion} 新于门店服务 v${input.serviceVersion}。已停止提交新的经营操作；请联系项目维护人员核对升级顺序。`,
        canRetry: true,
      };
    }
    if (updateState !== "UPDATE_AVAILABLE") return null;
    return {
      kind: "UPDATE_AVAILABLE",
      title: "发现新的 App 版本",
      description: `当前 App v${input.installedVersion}，服务端 v${input.serviceVersion}。请联系项目维护人员取得签名更新包。`,
      canRetry: false,
    };
  }
  return null;
}

export function NativeRuntimeNotice(): React.JSX.Element | null {
  const native = isNativeRuntime();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [appVersion, setAppVersion] = useState<string | null>(null);
  const [runtimeStatus, setRuntimeStatus] = useState<NativeRuntimeStatus>("pending");
  const [updateState, setUpdateState] = useState<NativeClientUpdateState>("POLICY_PENDING");
  const [sessionSafetyUnavailable, setSessionSafetyUnavailable] = useState(
    nativeSessionSafetyUnavailable(),
  );
  const mounted = useRef(false);
  const runtimeCheckId = useRef(0);
  const networkHandle = useRef<NativeNetworkHandle | null>(null);
  const networkRegistration = useRef<Promise<NativeNetworkHandle | null> | null>(null);
  const gatewayVersion = useQuery({
    queryKey: ["public-version"],
    queryFn: getPublicVersion,
    retry: 1,
    staleTime: 60_000,
    refetchInterval: 300_000,
    enabled: native,
  });

  const refreshNativeRuntime = useCallback(async () => {
    if (!native) return;
    const checkId = runtimeCheckId.current + 1;
    runtimeCheckId.current = checkId;
    setRuntimeStatus("pending");
    let listener = networkHandle.current
      ? Promise.resolve(networkHandle.current)
      : networkRegistration.current;
    if (!listener) {
      listener = listenForNativeNetwork((status) => {
        if (!mounted.current) return;
        setConnected(status.connected);
        if (status.connected) void gatewayVersion.refetch();
      }).catch(() => null);
      networkRegistration.current = listener;
      void listener.finally(() => {
        if (networkRegistration.current === listener) networkRegistration.current = null;
      });
    }
    const [snapshot, handle] = await Promise.all([readNativeRuntimeSnapshot(), listener]);
    if (!mounted.current || checkId !== runtimeCheckId.current) return;
    networkHandle.current = handle;
    setConnected(snapshot.connected);
    setAppVersion(snapshot.appVersion);
    setRuntimeStatus(nativeRuntimeStatusAfterCheck(snapshot, handle !== null));
  }, [gatewayVersion.refetch, native]);

  useEffect(() => {
    mounted.current = true;
    void refreshNativeRuntime();
    return () => {
      mounted.current = false;
      runtimeCheckId.current += 1;
      const handle = networkHandle.current;
      networkHandle.current = null;
      void handle?.remove().catch(() => undefined);
      const pendingRegistration = networkRegistration.current;
      networkRegistration.current = null;
      void pendingRegistration
        ?.then((pendingHandle) => {
          if (pendingHandle && pendingHandle !== handle) return pendingHandle.remove();
        })
        .catch(() => undefined);
    };
  }, [refreshNativeRuntime]);
  useEffect(() => subscribeNativeClientUpdateState(setUpdateState), []);
  useEffect(() => subscribeNativeSessionSafety(setSessionSafetyUnavailable), []);
  useEffect(() => {
    if (!native || typeof window === "undefined") return;
    const refresh = () => void gatewayVersion.refetch();
    window.addEventListener("billiard-native-version-refresh", refresh);
    return () => window.removeEventListener("billiard-native-version-refresh", refresh);
  }, [gatewayVersion.refetch, native]);

  const notice = nativeRuntimeNoticeModel({
    native,
    connected,
    runtimeStatus,
    gatewayStatus: gatewayVersion.status,
    installedVersion: appVersion,
    serviceVersion:
      gatewayVersion.data?.mobileClientPolicy?.latestVersion ??
      gatewayVersion.data?.productVersion ??
      null,
    minimumSupportedVersion:
      gatewayVersion.data?.mobileClientPolicy?.minimumSupportedVersion ?? null,
    updateState,
    sessionSafetyUnavailable,
  });
  useEffect(() => {
    const policy = gatewayVersion.data?.mobileClientPolicy;
    if (native && appVersion && policy) {
      setNativeClientUpdatePolicy(appVersion, policy);
    } else {
      clearNativeClientUpdatePolicy();
    }
  }, [appVersion, gatewayVersion.data, native]);
  useEffect(
    () => () => {
      clearNativeClientUpdatePolicy();
    },
    [],
  );
  const visible = notice !== null;
  useEffect(() => {
    document.body.classList.toggle("native-runtime-alert", visible);
    return () => document.body.classList.remove("native-runtime-alert");
  }, [visible]);
  if (!notice) return null;

  const LeadingIcon =
    notice.kind === "DEVICE_OFFLINE"
      ? WifiOff
      : notice.kind === "UPDATE_AVAILABLE" ||
          notice.kind === "UPDATE_REQUIRED" ||
          notice.kind === "SERVER_UPDATE_REQUIRED"
        ? Download
        : notice.kind === "VERSION_CHECK_PENDING"
          ? RefreshCw
          : AlertTriangle;

  return (
    <output
      className={`native-runtime-notice is-${notice.kind.toLowerCase().replaceAll("_", "-")}`}
      aria-live={
        notice.kind === "UPDATE_REQUIRED" || notice.kind === "SERVER_UPDATE_REQUIRED"
          ? "assertive"
          : "polite"
      }
      role={
        notice.kind === "UPDATE_REQUIRED" || notice.kind === "SERVER_UPDATE_REQUIRED"
          ? "alert"
          : "status"
      }
    >
      <LeadingIcon size={20} aria-hidden="true" />
      <div>
        <strong>{notice.title}</strong>
        <span>{notice.description}</span>
      </div>
      {notice.canRetry ? (
        <button
          className="native-runtime-retry"
          type="button"
          disabled={gatewayVersion.isFetching || runtimeStatus === "pending"}
          onClick={() => {
            void refreshNativeRuntime();
            void gatewayVersion.refetch();
          }}
        >
          <RefreshCw size={16} aria-hidden="true" />
          {gatewayVersion.isFetching || runtimeStatus === "pending"
            ? "检查中"
            : notice.kind === "UPDATE_REQUIRED" || notice.kind === "SERVER_UPDATE_REQUIRED"
              ? "重新检查版本"
              : "重新检查"}
        </button>
      ) : (
        <AlertTriangle size={18} aria-hidden="true" />
      )}
    </output>
  );
}
